import { useState, useEffect, useCallback, type FormEvent } from "react";
import { listUsers, createUser, activateUser, updateUser, assignWhatsAppRole } from "../services/auth";
import type { User, UserCreateRequest } from "../types/auth";
import { WHATSAPP_ROLE_CHOICES } from "../types/auth";

const PAGE_SIZE = 10;

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState<UserCreateRequest>({ username: "", email: "", password: "", first_name: "", last_name: "" });
  const [creating, setCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ username: "", first_name: "", last_name: "", email: "", password: "", is_staff: false });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listUsers({ page: pageNum, page_size: PAGE_SIZE }) as Record<string, unknown>;
      const results = resp.data || resp.results || [];
      setUsers(Array.isArray(results) ? results as User[] : []);
      setTotal(typeof resp.count === 'number' ? resp.count as number : 0);
      setTotalPages(typeof resp.numero_paginas === 'number' ? resp.numero_paginas as number : 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [page, fetchData]);

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault(); setCreating(true); setError(null); setSuccessMsg(null);
    try {
      await createUser(newUser);
      setSuccessMsg(`Usuario "${newUser.username}" creado`);
      setShowCreate(false);
      setNewUser({ username: "", email: "", password: "", first_name: "", last_name: "" });
      setPage(1);
    } catch (err) { setError(err instanceof Error ? err.message : "Error al crear usuario"); }
    finally { setCreating(false); }
  };

  const openEditPopup = (user: User) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      password: "",
      is_staff: user.is_staff,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSavingEdit(true); setError(null);
    try {
      const payload: Record<string, unknown> = {
        username: editForm.username,
        email: editForm.email,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        is_staff: editForm.is_staff,
      };
      if (editForm.password) payload.password = editForm.password;
      await updateUser(editingUser.id, payload);
      setSuccessMsg(`Usuario "${editingUser.username}" actualizado`);
      setEditingUser(null);
      await fetchData(page);
    } catch (err) { setError(err instanceof Error ? err.message : "Error al actualizar usuario"); }
    finally { setSavingEdit(false); }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await activateUser(user.id, { is_active: !user.is_active });
      setSuccessMsg(`Usuario "${user.username}" ${!user.is_active ? "activado" : "desactivado"}`);
      await fetchData(page);
    } catch (err) { setError(err instanceof Error ? err.message : "Error al cambiar estado"); }
  };

  const handleWhatsAppRole = async (user: User, rol: string, action: 'assign' | 'unassign') => {
    try {
      await assignWhatsAppRole({ user_id: user.id, rol, action });
      setSuccessMsg(`Rol "${rol}" ${action === 'assign' ? 'asignado a' : 'quitado de'} ${user.username}`);
      await fetchData(page);
    } catch (err) { setError(err instanceof Error ? err.message : `Error al ${action === 'assign' ? 'asignar' : 'quitar'} rol`); }
  };

  const hasWhatsAppRole = (user: User, rol: string): boolean => user.whatsapp_roles?.some(r => r.rol === rol) ?? false;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Gestión de Usuarios</h2>
          <p className="page-subtitle">{total} usuario{total !== 1 ? "s" : ""} registrado{total !== 1 ? "s" : ""}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Nuevo Usuario
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {showCreate && (
        <div className="card form-card">
          <h3>Crear nuevo usuario</h3>
          <form onSubmit={handleCreateUser} className="create-user-form">
            <div className="form-row three-col">
              <div className="form-group">
                <label>Usuario *</label>
                <input type="text" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} placeholder="Nombre de usuario" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="correo@ejemplo.com" required />
              </div>
              <div className="form-group">
                <label>Contraseña *</label>
                <input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Contraseña" required />
              </div>
            </div>
            <div className="form-row two-col">
              <div className="form-group">
                <label>Nombres</label>
                <input type="text" value={newUser.first_name || ""} onChange={e => setNewUser({ ...newUser, first_name: e.target.value })} placeholder="Nombres" />
              </div>
              <div className="form-group">
                <label>Apellidos</label>
                <input type="text" value={newUser.last_name || ""} onChange={e => setNewUser({ ...newUser, last_name: e.target.value })} placeholder="Apellidos" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? "Creando..." : "Crear Usuario"}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner" /><span>Cargando usuarios...</span></div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          <h3>No hay usuarios registrados</h3>
          <p>Crea el primer usuario para comenzar</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="users-table">
              <thead>
                <tr><th>Usuario</th><th>Roles WhatsApp</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.username}</strong>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
                        {user.email}
                        {user.is_staff ? <span className="group-badge" style={{ marginLeft: 6 }}>Admin</span> : null}
                        {!user.is_active ? <span style={{ color: "var(--accent-danger)", marginLeft: 6 }}>Inactivo</span> : null}
                      </div>
                    </td>
                    <td>
                      <div className="whatsapp-roles-actions">
                        {WHATSAPP_ROLE_CHOICES.map((role) => {
                          const assigned = hasWhatsAppRole(user, role.value);
                          return (
                            <button key={role.value} className={`role-chip ${assigned ? "role-chip-active" : "role-chip-inactive"}`}
                              onClick={() => handleWhatsAppRole(user, role.value, assigned ? "unassign" : "assign")}
                              title={`${assigned ? "Quitar" : "Asignar"} rol ${role.label}`}>
                              {role.label}{assigned ? " ✓" : " +"}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions-row">
                        <button className="btn btn-sm btn-secondary" onClick={() => openEditPopup(user)} title="Editar">✏️</button>
                        <button className={`btn btn-sm ${user.is_active ? "btn-danger" : "btn-primary"}`}
                          onClick={() => handleToggleActive(user)} title={user.is_active ? "Desactivar" : "Activar"}>
                          {user.is_active ? "🚫" : "✅"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-bar">
            <button className="btn btn-sm btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>← Anterior</button>
            <span className="pagination-info">Página {page} de {totalPages}</span>
            <button className="btn btn-sm btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Siguiente →</button>
          </div>
        </>
      )}

      {/* Edit user popup */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "560px" }}>
            <div className="modal-header">
              <h3>Editar: {editingUser.username}</h3>
              <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row three-col">
                <div className="form-group">
                  <label>Usuario</label>
                  <input type="text" value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nueva contraseña</label>
                  <input type="password" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="Dejar vacío para no cambiar" />
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label>Nombres</label>
                  <input type="text" value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Apellidos</label>
                  <input type="text" value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={editForm.is_staff} onChange={e => setEditForm({ ...editForm, is_staff: e.target.checked })} />
                  Administrador
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? "Guardando..." : "Guardar Cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}